import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import ValidatedField from 'components/controls/validated-field';
import ValidatedDateField from 'components/controls/validated-date-field';
import ValidatedMultiSelectField from 'components/controls/validated-multi-select-field';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { translationApi  } from 'actions/translation-api';
import { changeSearchCriteria, changeClient } from 'store/client/slice.measurement-list-filter';
import { getDosemeterTypeDescription, DOSEMETER_TYPE_ENVIRONMENTAL, DOSEMETER_TYPE_PERSONAL, DOSEMETER_TYPE_PERSONAL_WRIST } from 'data/enums/dosemeter-type-enum';
import getMeasurementStatusDescription, { MEASUREMENT_STATUS_TO_SEND, MEASUREMENT_STATUS_IN_PROGRESS, MEASUREMENT_STATUS_COMPLETED, MEASUREMENT_STATUS_DOSEMETER_LOST } from 'data/enums/measurement-status-enum';
import { tryParseInt } from 'utils/number-utils';

const ClientDosemeterMeasurementSearchFilter = () => {
    const dispatch = useDispatch();
    const params = useParams();
	const clientIdString = params.id;
	const clientId = tryParseInt(clientIdString);
    const clientPreSelected = clientId !== null;

    const [submitEnabled, setSubmitEnabled] = useState(true);
	const measurementListFilter = useSelector(state => state.measurementListFilter);
    const translations = useSelector(translationApi.endpoints.getTranslations.select()).data ?? {};

	const filterForCurrentClient = (isNaN(clientId) && isNaN(measurementListFilter.filterForClient)) || clientId === measurementListFilter.filterForClient;
	if (!filterForCurrentClient) {
		dispatch(changeClient(clientId));
	}

    const handleSubmit = fields => {
        const filter = {
            clientId: tryParseInt(fields?.clientId),
            clientDosemeterNumber: fields?.clientDosemeterNumber,
            clientDosemeterOldNumber: fields?.clientDosemeterOldNumber,
            clientName: fields?.clientName,
            clientVatin: fields?.clientVatin,
            periodStart: fields?.periodStart,
            periodEnd: fields?.periodEnd,
            dosemeterUserFirstName: fields?.dosemeterUserFirstName,
            dosemeterUserLastName: fields?.dosemeterUserLastName,
            dosemeterTypes: fields?.dosemeterTypes,
            statuses: fields?.statuses,
            page: 1,
        };
        dispatch(changeSearchCriteria(filter));

        setSubmitEnabled(false);
        setTimeout(() => setSubmitEnabled(true), 1000);
    };

    const dosemeterTypesToSelect = [
        { value: DOSEMETER_TYPE_ENVIRONMENTAL, label: getDosemeterTypeDescription(DOSEMETER_TYPE_ENVIRONMENTAL, translations) },
        { value: DOSEMETER_TYPE_PERSONAL, label: getDosemeterTypeDescription(DOSEMETER_TYPE_PERSONAL, translations) },
        { value: DOSEMETER_TYPE_PERSONAL_WRIST, label: getDosemeterTypeDescription(DOSEMETER_TYPE_PERSONAL_WRIST, translations) },
    ];

    const statusesToSelect = [
        { value: MEASUREMENT_STATUS_TO_SEND, label: getMeasurementStatusDescription(MEASUREMENT_STATUS_TO_SEND, translations) },
        { value: MEASUREMENT_STATUS_IN_PROGRESS, label: getMeasurementStatusDescription(MEASUREMENT_STATUS_IN_PROGRESS, translations) },
        { value: MEASUREMENT_STATUS_COMPLETED, label: getMeasurementStatusDescription(MEASUREMENT_STATUS_COMPLETED, translations) },
        { value: MEASUREMENT_STATUS_DOSEMETER_LOST, label: getMeasurementStatusDescription(MEASUREMENT_STATUS_DOSEMETER_LOST, translations) },
    ];

    const validationSchema = Yup.object().shape({
        clientId: Yup.number(),
        clientDosemeterNumber: Yup.number(),
        clientName: Yup.string(),
        clientVatin: Yup.string()
            .max(10, translations.warningValueTooLong),
        dosemeterTypes: Yup.array(),
        statuses: Yup.array(),
    });

    const initialValues = {
        clientId: filterForCurrentClient ? measurementListFilter?.clientId?.toString().padStart(4, '0') ?? "" : clientPreSelected ? clientId : "",
        clientDosemeterNumber: filterForCurrentClient ? measurementListFilter?.clientDosemeterNumber ?? "" : "",
        clientName: filterForCurrentClient ? measurementListFilter?.clientName ?? "" : "",
        clientVatin: filterForCurrentClient ? measurementListFilter?.clientVatin ?? "" : "",
        periodStart: filterForCurrentClient ? measurementListFilter?.periodStart ?? "" : "",
        periodEnd: filterForCurrentClient ? measurementListFilter?.periodEnd ?? "" : "",
        dosemeterUserFirstName: filterForCurrentClient ? measurementListFilter?.dosemeterUserFirstName ?? "" : "",
        dosemeterUserLastName: filterForCurrentClient ? measurementListFilter?.dosemeterUserLastName ?? "" : "",
        dosemeterTypes: filterForCurrentClient ? measurementListFilter?.dosemeterTypes ?? [] : [],
        statuses: filterForCurrentClient ? measurementListFilter?.statuses ?? [] : [],
    };

    const randomAutoComplete = Math.random().toString(36).replace(/[^a-z]+/g, "").substr(0, 5);
    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={fields => { handleSubmit(fields) }}>
            {props => (
                <Form autoComplete={randomAutoComplete}>
                    <div className="col-12 mb-4">
                        <div className="card border-light shadow-sm">
                            <div className="card-body">
                                <h2 className="h6 mb-2">{translations.filters}</h2>
                                <div className="row">
                                    {clientPreSelected &&
                                        <Field name="clientId" type="hidden" />
                                    }
                                    {clientPreSelected &&
                                        <div className="col-lg-2 col-md-12 mb-2">
                                            <ValidatedField name="clientDosemeterNumber" placeholder={translations.dosemeterNumber} maxLength="4" {...props} />
                                        </div>
                                    }
                                    {!clientPreSelected &&
                                        <div className="col-lg-1 col-md-12 mb-2">
                                            <ValidatedField name="clientId" placeholder={translations.clientNumberShort} maxLength="4" {...props} />
                                        </div>
                                    }
                                    {!clientPreSelected &&
                                        <div className="col-lg-1 col-md-12 mb-2">
                                            <ValidatedField name="clientDosemeterNumber" placeholder={translations.dosemeterNumberShort} maxLength="4" {...props} />
                                        </div>
                                    }
                                    {!clientPreSelected &&
                                        <div className="col-lg-1 col-md-12 mb-2">
                                            <ValidatedField name="clientDosemeterOldNumber" placeholder="Daw. nr doz." maxLength="4" {...props} />
                                        </div>
                                    }
                                    {!clientPreSelected &&
                                        <div className="col-lg-2 col-md-12 mb-2">
                                            <ValidatedField name="clientName" placeholder={translations.clientName} {...props} />
                                        </div>
                                    }
                                    {!clientPreSelected &&
                                        <div className="col-lg-2 col-md-12 mb-2">
                                            <ValidatedField name="clientVatin" placeholder={translations.vatin} maxLength="10" {...props} />
                                        </div>
                                    }
                                    <div className="col-lg-2 col-md-12 mb-2">
                                        <ValidatedDateField name="periodStart" placeholder={translations.measurementPeriodStart} {...props} />
                                    </div>
                                    <div className="col-lg-2 col-md-12 mb-2">
                                        <ValidatedDateField name="periodEnd" placeholder={translations.measurementPeriodEnd} {...props} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-lg-2 col-md-12 mb-2">
                                        <ValidatedField name="dosemeterUserFirstName" placeholder={translations.firstName} {...props} />
                                    </div>
                                    <div className="col-lg-2 col-md-12 mb-2">
                                        <ValidatedField name="dosemeterUserLastName" placeholder={translations.lastName} {...props} />
                                    </div>
                                    <div className="col-lg-3 col-md-12 mb-2">
                                        <ValidatedMultiSelectField name="dosemeterTypes" placeholder={translations.type} options={dosemeterTypesToSelect} isMulti={true}
                                            onChange={props.setFieldValue}
                                            onBlur={props.setFieldTouched} {...props} />
                                    </div>
                                    <div className="col-lg-3 col-md-12 mb-2">
                                        <ValidatedMultiSelectField name="statuses" placeholder={translations.status} options={statusesToSelect} isMulti={true}
                                            onChange={props.setFieldValue}
                                            onBlur={props.setFieldTouched} {...props} />
                                    </div>
                                    <div className="col mb-2">
                                        <button id="searchButton" type="submit" className="btn btn-secondary float-end" disabled={submitEnabled ? null : 'disabled'} ><FontAwesomeIcon icon={faSearch} className="me-2" />{translations.actionSearch}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>)}
        </Formik>
    )
}

export default ClientDosemeterMeasurementSearchFilter;
